import { useState, useMemo } from 'react';
import { Head, Link, router, useForm } from '@inertiajs/react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Upload, 
  Edit, 
  Trash2, 
  Package,
  GitBranch,
  Merge,
  Hash,
  FileText,
  Code,
  Database,
  HelpCircle,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ParseJob {
  id: number;
  name: string;
}

interface ExtractJob {
  id: number;
  name: string;
}

interface Chunk {
  id: number;
  page_number: number;
  content: string;
  citation?: string;
  embed?: string;
  metadata?: Record<string, any>;
}

interface ChunkCollection {
  id: number;
  name: string;
  chunks_count: number;
  parse_job_id: number;
  extract_job_id?: number;
  chunks: Chunk[];
}

interface Document {
  id: number;
  name: string;
  completed?: boolean;
}

interface ChunkCollectionEditProps {
  document: Document;
  chunk_collection: ChunkCollection;
  parse_job: ParseJob;
  extract_job?: ExtractJob;
  flash?: {
    notice?: string | null;
    alert?: string | null;
  };
}

type SortField = 'page_number' | 'size' | 'none';
type SortDirection = 'asc' | 'desc';

export default function ChunkCollectionEdit({ 
  document, 
  chunk_collection, 
  parse_job,
  extract_job,
  flash
}: ChunkCollectionEditProps) {
  const [sortField, setSortField] = useState<SortField>('none');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const { post, processing } = useForm();

  const sortedChunks = useMemo(() => {
    if (sortField === 'none') return chunk_collection.chunks;
    
    return [...chunk_collection.chunks].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'page_number':
          aValue = a.page_number;
          bValue = b.page_number;
          break;
        case 'size':
          aValue = a.content.length;
          bValue = b.content.length;
          break;
        default:
          return 0;
      }
      
      const result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortDirection === 'desc' ? -result : result;
    });
  }, [chunk_collection.chunks, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-3 w-3" /> : 
      <ArrowDown className="h-3 w-3" />;
  };

  const getSizeColor = (length: number) => {
    if (length < 300) return 'bg-red-100 text-red-800';
    if (length < 2000) return 'bg-yellow-100 text-yellow-800';
    if (length > 4000) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const handleUploadChunks = () => {
    post(`/documents/${document.id}/chunk_collections/${chunk_collection.id}/upload_document_chunks`);
  };

  const handleDeleteChunk = (chunkId: number) => {
    if (confirm('Are you sure you want to delete this chunk?')) {
      router.delete(`/documents/${document.id}/chunks/${chunkId}`);
    }
  };

  const handleScheduleTestQuestion = (chunkId: number) => {
    post(`/documents/${document.id}/chunk_collections/${chunk_collection.id}/chunks/${chunkId}/schedule_test_question`);
  };


  return (
    <>
      <Head title={`Chunks for ${document.name} (Collection #${chunk_collection.id})`} />
      
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          {flash?.alert ? (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{flash.alert}</AlertDescription>
            </Alert>
          ) : null}
          {flash?.notice ? (
            <Alert className="border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-100">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle>Queued</AlertTitle>
              <AlertDescription>{flash.notice}</AlertDescription>
            </Alert>
          ) : null}

          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: `Collection #${chunk_collection.id}`, current: true }
            ]}
          />

          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold">
              Chunks for {document.name}
            </h1>
            <div className="text-lg text-gray-600">
              Collection #{chunk_collection.id}
              {chunk_collection.name && (
                <span> - {chunk_collection.name}</span>
              )}
            </div>
          </div>

          {/* Collection Info */}
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    <GitBranch className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold">Parse Job</span>
                  </div>
                  <Button asChild variant="link" className="text-blue-600">
                    <Link href={`/documents/${document.id}/parse_jobs/${chunk_collection.parse_job_id}/edit`}>
                      #{chunk_collection.parse_job_id}
                      {parse_job && ` - ${parse_job.name}`}
                    </Link>
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    <Merge className="h-5 w-5 text-purple-600" />
                    <span className="font-semibold">Extract Job</span>
                  </div>
                  {chunk_collection.extract_job_id && extract_job ? (
                    <Button asChild variant="link" className="text-blue-600">
                      <Link href={`/documents/${document.id}/extract_jobs/${chunk_collection.extract_job_id}/edit`}>
                        #{chunk_collection.extract_job_id}
                        {extract_job && ` - ${extract_job.name}`}
                      </Link>
                    </Button>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    <Package className="h-5 w-5 text-green-600" />
                    <span className="font-semibold">Chunks Count</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {chunk_collection.chunks_count}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
            
            {/* Upload to Vector DB */}
            <Card className="flex-1 max-w-md">
              <CardContent className="p-4">
                <Button 
                  onClick={handleUploadChunks}
                  disabled={processing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload to Vector DB
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Chunks Table */}
          <Card>
            <CardContent className="p-0">
              {chunk_collection.chunks.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSort('page_number')}
                            className="h-auto p-0 font-semibold hover:bg-transparent"
                          >
                            <div className="flex items-center gap-2">
                              <Hash className="h-4 w-4" />
                              Page #
                              {getSortIcon('page_number')}
                            </div>
                          </Button>
                        </TableHead>
                        <TableHead className="w-20">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSort('size')}
                            className="h-auto p-0 font-semibold hover:bg-transparent"
                          >
                            <div className="flex items-center gap-2">
                              Size
                              {getSortIcon('size')}
                            </div>
                          </Button>
                        </TableHead>
                        <TableHead className="w-48">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Citation
                          </div>
                        </TableHead>
                        <TableHead className="w-64">Content</TableHead>
                        <TableHead className="w-48">
                          <div className="flex items-center gap-2">
                            <Code className="h-4 w-4" />
                            Embed
                          </div>
                        </TableHead>
                        <TableHead className="w-32">
                          <div className="flex items-center gap-2">
                            <Database className="h-4 w-4" />
                            Metadata
                          </div>
                        </TableHead>
                        <TableHead className="w-32">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedChunks.map((chunk) => (
                        <TableRow key={chunk.id}>
                          <TableCell className="font-medium">
                            {chunk.page_number}
                          </TableCell>
                          <TableCell>
                            <Badge className={getSizeColor(chunk.content.length)}>
                              {chunk.content.length}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-48 break-words">
                            <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                              {chunk.citation || '—'}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-64 break-words">
                            <div className="text-sm text-gray-800 whitespace-pre-wrap max-h-32 overflow-y-auto">
                              {chunk.content}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-48 break-words">
                            <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                              {chunk.embed || '—'}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-32 break-words">
                            <div className="text-xs font-mono text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                              {chunk.metadata && Object.keys(chunk.metadata).length > 0 
                                ? JSON.stringify(chunk.metadata, null, 2)
                                : '—'
                              }
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-2">
                              <Button asChild size="sm" variant="outline">
                                <Link href={`/documents/${document.id}/chunks/${chunk.id}/edit`}>
                                  <Edit className="h-3 w-3 mr-1" />
                                  Edit
                                </Link>
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteChunk(chunk.id)}
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Delete
                              </Button>
                              
                              {document.completed && (
                                <>
                                  <Button asChild size="sm" variant="outline">
                                    <Link href={`/documents/${document.id}/chunk_collections/${chunk_collection.id}/chunks/${chunk.id}/test_questions`}>
                                      <HelpCircle className="h-3 w-3 mr-1" />
                                      Questions
                                    </Link>
                                  </Button>
                                  <Button 
                                    size="sm"
                                    onClick={() => handleScheduleTestQuestion(chunk.id)}
                                    className="bg-blue-600 hover:bg-blue-700"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Generate Question
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-4">No chunks found in this collection.</p>
                  <Button asChild>
                    <Link href={`/documents/${document.id}/chunk_collections/new`}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Collection
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Actions */}
          <div className="flex justify-center">
            <Button asChild className="bg-purple-600 hover:bg-purple-700">
              <Link href={`/documents/${document.id}/chunk_collections/new`}>
                <Plus className="h-4 w-4 mr-2" />
                Create New Collection
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}